import { calculatePolygonCenter, GeoJSONPoint } from '../GeofenceManager/GeofenceManager.helpers';

// Mean Earth radius in meters (matches @turf's WGS84 mean radius).
const EARTH_RADIUS_METERS = 6371008.8;
const toRadians = (degrees) => (degrees * Math.PI) / 180;

// Great-circle distance between two [lng, lat] points, in meters (haversine).
// Inlined rather than using @turf/distance: that package is turf v7 (ESM) which
// the repo's jest 23 test runner cannot resolve.
const metersBetween = ([lng1, lat1], [lng2, lat2]) => {
    const dLat = toRadians(lat2 - lat1);
    const dLng = toRadians(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) ** 2;
    // Clamp to [0,1] so floating-point drift can't make asin(sqrt(a)) return NaN.
    const clamped = Math.min(1, Math.max(0, a));
    return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(clamped));
};

// TerraDraw returns closed GeoJSON rings (last point === first); drop the
// duplicate before averaging so the centroid is unbiased.
const ringToCenter = (ring) => {
    const open = ring.slice(0, -1);
    return calculatePolygonCenter(open.map(([lng, lat]) => ({ lat, lng })));
};

// Guard external (TerraDraw) feature output: return the outer ring only if it is a
// closed polygon ring (>= 3 points + closing point), otherwise null.
const getPolygonRing = (feature) => {
    const ring = feature?.geometry?.coordinates?.[0];
    if (!Array.isArray(ring) || ring.length < 4) return null;
    return ring;
};

// TerraDraw polygon feature → our polygonalGeofence model. Returns null if the
// feature is malformed so callers can skip it rather than throw.
export const polygonFeatureToGeofence = (feature) => {
    const ring = getPolygonRing(feature);
    if (!ring) return null;
    const center = ringToCenter(ring);
    if (!center) return null;
    return {
        type: 'polygonalGeofence',
        center: GeoJSONPoint([Number(center.lng), Number(center.lat)]),
        geometry: GeoJSONPoint(feature.geometry.coordinates, 'Polygon'),
    };
};

// TerraDraw circle feature (a polygon approximation) → our radialGeofence model.
// radius is reconstructed as the distance from the centroid to any vertex.
// Returns null if the feature is malformed.
export const circleFeatureToGeofence = (feature) => {
    const ring = getPolygonRing(feature);
    if (!ring) return null;
    const center = ringToCenter(ring);
    if (!center) return null;
    const centerCoord = [Number(center.lng), Number(center.lat)];
    return {
        type: 'radialGeofence',
        center: GeoJSONPoint(centerCoord),
        radius_meters: metersBetween(centerCoord, ring[0]),
    };
};
